/**
 * Data Integrity Badge Component
 * 
 * Displays quarantine + orphan counts at a glance in War Room.
 * Clicking navigates to Admin → Quarantine tab.
 * 
 * P1 HARDENING: Provides immediate visibility into data integrity issues.
 * 
 * Visual states:
 * - Healthy (green): No quarantined appointments or orphans
 * - Warning (amber): 1-9 total issues (quarantined + orphans)
 * - Critical (red): 10+ total issues
 * 
 * Badge text:
 * - Healthy: "Data Healthy"
 * - Issues: "X Quarantined" (if quarantine is primary) or "X Data Issues" (if mixed)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { getStoredUser } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { 
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';

/**
 * Data Integrity Badge
 * 
 * Shows quarantine and orphan counts with hover details.
 * Cached for 60 seconds to avoid excessive API calls.
 */
export default function DataIntegrityBadge({ className = "" }) {
  const navigate = useNavigate();
  const { selectedTenantId } = useTenant();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(0);
  const [hasPermission, setHasPermission] = useState(true);

  // Cache duration: 60 seconds
  const CACHE_DURATION_MS = 60 * 1000;

  const fetchDiagnostics = useCallback(async (force = false) => {
    const now = Date.now();
    
    // Skip if cached data is still fresh (unless forced)
    if (!force && data && (now - lastFetch) < CACHE_DURATION_MS) {
      return;
    }

    try {
      setLoading(true);
      const res = await api.get("/admin/diagnostics/data-integrity");
      setData(res.data);
      setError(null);
      setLastFetch(now);
      setHasPermission(true);
    } catch (err) {
      if (err.response?.status === 403) {
        setHasPermission(false);
      } else {
        setError(err.response?.data?.detail || 'Failed to load');
      }
    } finally {
      setLoading(false);
    }
  }, [data, lastFetch]);

  // Check if user has admin permissions
  useEffect(() => {
    const user = getStoredUser();
    const isAdmin = user?.is_super_admin || user?.is_admin;
    if (!isAdmin) {
      setHasPermission(false);
      setLoading(false);
    }
  }, []);

  // Fetch on mount and when tenant changes
  useEffect(() => {
    if (hasPermission) {
      fetchDiagnostics(true); // Force refresh on tenant change
    }
  }, [selectedTenantId, hasPermission]);

  // Don't render if user lacks permission
  if (!hasPermission) {
    return null;
  }

  // Don't render while loading initial data
  if (loading && !data) {
    return null;
  }

  // Don't render on error
  if (error) {
    return null;
  }

  const quarantined = data?.quarantine?.currently_quarantined || 0;
  const totalOrphans = data?.total_orphans || 0;
  const totalIssues = quarantined + totalOrphans;
  const healthStatus = data?.health_status || "HEALTHY";

  const isHealthy = healthStatus === "HEALTHY" && quarantined === 0 && totalOrphans === 0;
  const isCritical = healthStatus === "CRITICAL" || totalIssues >= 10;
  const isWarning = !isHealthy && !isCritical;

  // Navigate to Admin Quarantine tab
  const handleClick = () => {
    navigate('/app/admin?tab=quarantine');
  };

  // Badge styling based on status - matches BottleneckBadge and SystemHealthBadge
  const getBadgeClass = () => {
    if (isCritical) return "bg-red-500/20 text-red-300 border-red-500/30 hover:bg-red-500/30";
    if (isWarning) return "bg-amber-500/20 text-amber-300 border-amber-500/30 hover:bg-amber-500/30";
    return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/30";
  };

  // Icon size matches other badges (h-3 w-3)
  const getIcon = () => {
    if (isCritical) return <ShieldAlert className="h-3 w-3" />;
    if (isWarning) return <AlertTriangle className="h-3 w-3" />;
    return <CheckCircle2 className="h-3 w-3" />;
  };

  // Badge text - descriptive like other badges
  const getBadgeText = () => {
    if (isHealthy) return "Data Healthy";
    // If quarantine is the primary issue, be specific
    if (quarantined > 0 && totalOrphans === 0) {
      return `${quarantined} Quarantined`;
    }
    // If orphans only
    if (quarantined === 0 && totalOrphans > 0) {
      return `${totalOrphans} Orphan${totalOrphans !== 1 ? 's' : ''}`;
    }
    // Mixed issues
    return `${totalIssues} Data Issues`;
  };

  const getStatusLabel = () => {
    if (isCritical) return "Critical";
    if (isWarning) return "Warning";
    return "Healthy";
  };

  return (
    <HoverCard openDelay={100} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Badge
          variant="outline"
          className={`cursor-pointer transition-colors ${getBadgeClass()} ${className}`}
          onClick={handleClick}
          data-testid="data-integrity-badge"
        >
          <div className="flex items-center gap-1.5">
            {getIcon()}
            <span className="text-xs">{getBadgeText()}</span>
          </div>
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent 
        side="bottom"
        align="end"
        className="w-72 bg-slate-950 border-white/10 text-slate-100 p-4 z-50"
        data-testid="data-integrity-hover"
      >
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${
              isCritical ? 'bg-red-500/20' : 
              isWarning ? 'bg-amber-500/20' : 
              'bg-emerald-500/20'
            }`}>
              {isCritical ? <ShieldAlert className="h-5 w-5 text-red-400" /> :
               isWarning ? <AlertTriangle className="h-5 w-5 text-amber-400" /> :
               <CheckCircle2 className="h-5 w-5 text-emerald-400" />}
            </div>
            <div>
              <div className={`text-sm font-medium ${
                isCritical ? 'text-red-400' : 
                isWarning ? 'text-amber-400' : 
                'text-emerald-400'
              }`}>
                {isHealthy ? 'Data Integrity OK' : 'Data Integrity Issues'}
              </div>
              <div className="text-xs text-slate-400">
                {isHealthy 
                  ? 'No quarantined appointments or orphan records.'
                  : `${totalIssues} issue${totalIssues !== 1 ? 's' : ''} requiring attention.`
                }
              </div>
            </div>
          </div>

          {/* Stats - only show if there are issues */}
          {!isHealthy && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-slate-500 mb-0.5">Quarantined</div>
                <div className={`font-semibold ${quarantined > 0 ? 'text-amber-400' : 'text-slate-200'}`}>
                  {quarantined}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-0.5">Orphans</div>
                <div className={`font-semibold ${totalOrphans > 0 ? 'text-amber-400' : 'text-slate-200'}`}>
                  {totalOrphans}
                </div>
              </div>
            </div>
          )}

          {/* Breakdown if issues exist */}
          {totalOrphans > 0 && data?.orphan_counts && (
            <div className="pt-2 border-t border-white/10">
              <div className="text-xs text-slate-500 mb-2">Breakdown</div>
              <div className="space-y-1 text-xs">
                {data.orphan_counts.appointments_missing_lead > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Appointments (no lead)</span>
                    <span className="text-slate-300">{data.orphan_counts.appointments_missing_lead}</span>
                  </div>
                )}
                {data.orphan_counts.appointments_quarantined > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Appointments (quarantined)</span>
                    <span className="text-slate-300">{data.orphan_counts.appointments_quarantined}</span>
                  </div>
                )}
                {data.orphan_counts.clients_missing_primary_lead > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Clients (no lead)</span>
                    <span className="text-slate-300">{data.orphan_counts.clients_missing_primary_lead}</span>
                  </div>
                )}
                {data.orphan_counts.household_members_incomplete > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Household (incomplete)</span>
                    <span className="text-slate-300">{data.orphan_counts.household_members_incomplete}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action hint - only show if there are issues */}
          {!isHealthy && (
            <div className="pt-2 border-t border-white/10">
              <button
                onClick={handleClick}
                className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                data-testid="data-integrity-link"
              >
                <span>View Quarantine Queue</span>
                <ExternalLink className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
